import { DateTime } from '@ksyos/date-time';
import { isEqual } from 'lodash';
import type { IServiceGroupSetting, IServiceOrganizationGroupHistory } from '@frontend/components/interfaces';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ServiceCode } from '@common/enums';
import { isActiveOnDate } from '@common/utils/isActiveOnDate';
import { validateDatetimeOverlap } from '@common/validation/utils/validateDatetimeOverlap';
import { emptyValidationErrors } from '@frontend/utils/validation';

export function validateGroupHistoriesTimeOverlap(
    serviceOrganizationGroupHistoryEditVersion: IServiceOrganizationGroupHistory[] = [],
    errors: ValidationErrors
) {
    let updatedErrors = errors;

    if (serviceOrganizationGroupHistoryEditVersion) {
        const groupsToValidate = serviceOrganizationGroupHistoryEditVersion
            .filter((history) => !history.isDeleted && history.startDate)
            .map((history) => ({
                ...history,
                errors: emptyValidationErrors,
            }));
        const validatedGroups = validateDatetimeOverlap(groupsToValidate);

        for (const validatedGroup of validatedGroups) {
            const index = serviceOrganizationGroupHistoryEditVersion.findIndex((sOGH) => sOGH.id === validatedGroup.id);
            if (
                validatedGroup.startDate &&
                validatedGroup.endDate &&
                DateTime.from(validatedGroup.startDate).isAfter(DateTime.from(validatedGroup.endDate))
            ) {
                updatedErrors = updatedErrors.set(`serviceOrganizationGroupHistory.${index}.endDate`, 'koi_endDate');
            } else if (validatedGroup.errors.size > 0) {
                validatedGroup.errors.forEach(
                    (error) =>
                        (updatedErrors = updatedErrors.set(`serviceOrganizationGroupHistory.${index}.endDate`, error))
                );
            } else {
                updatedErrors = updatedErrors.remove(`serviceOrganizationGroupHistory.${index}.endDate`);
            }
            updatedErrors = updatedErrors.remove(`serviceOrganizationGroupHistory.${index}.startDate`);
        }
    }

    return updatedErrors;
}

export function isServiceOrganizationGroupHistoriesChanged(
    originalServiceGroupHistory: IServiceOrganizationGroupHistory[] = [],
    updatedServiceOrganizationGroupHistory: IServiceOrganizationGroupHistory[] = [],
    setIsServiceGroupDeleted: (isServiceGroupDeleted: boolean) => void,
    setIsServiceGroupChanged: (isServiceGroupChanged: boolean) => void
) {
    const isDeleted = isServiceOrganizationGroupDeleted(
        originalServiceGroupHistory,
        updatedServiceOrganizationGroupHistory
    );

    const isChanged = isServiceOrganizationGroupChanged(
        originalServiceGroupHistory,
        updatedServiceOrganizationGroupHistory
    );
    setIsServiceGroupDeleted(isDeleted);
    setIsServiceGroupChanged(isChanged);

    return isDeleted || isChanged;
}

export function isServiceOrganizationGroupChanged(
    originalServiceGroupHistory: IServiceOrganizationGroupHistory[] = [],
    updatedServiceOrganizationGroupHistory: IServiceOrganizationGroupHistory[] = []
) {
    const originalServiceGroupHistoryWithoutDeleted = originalServiceGroupHistory.map(removeIsDeleted);
    const updatedServiceGroupHistoryWithoutDeleted = updatedServiceOrganizationGroupHistory.map(removeIsDeleted);

    return !isEqual(originalServiceGroupHistoryWithoutDeleted, updatedServiceGroupHistoryWithoutDeleted);
}

function removeIsDeleted(history: IServiceOrganizationGroupHistory) {
    const { isDeleted: _isDeleted, ...rest } = history;

    return rest;
}

export function isServiceOrganizationGroupDeleted(
    originalServiceGroupHistory: IServiceOrganizationGroupHistory[] = [],
    updatedServiceOrganizationGroupHistory: IServiceOrganizationGroupHistory[] = []
) {
    return (
        originalServiceGroupHistory.filter((osgh) => osgh.isDeleted).length !==
        updatedServiceOrganizationGroupHistory.filter((osgh) => osgh.isDeleted).length
    );
}

export function validateServiceGroupSettingId(
    serviceCode: ServiceCode,
    currentServiceGroup: IServiceGroupSetting | null,
    hasServiceProviders: boolean,
    errors: ValidationErrors
) {
    if (serviceCode === ServiceCode.GGZOembl && !currentServiceGroup && hasServiceProviders) {
        return errors.set('serviceGroupSettingId', 'noCurrentServiceOrganizationGroupEmbloom');
    } else {
        return errors.remove('serviceGroupSettingId');
    }
}

export function findAndSetCurrentServiceGroup(
    organizationGroupHistory: IServiceOrganizationGroupHistory[],
    setCurrentServiceGroup: (currentServiceGroup: IServiceGroupSetting | null) => void
) {
    const activeServiceGroup = organizationGroupHistory.find(
        (groupHistory) => isActiveOnDate(groupHistory, new Date()) && !groupHistory.isDeleted
    );
    setCurrentServiceGroup(activeServiceGroup?.serviceGroupSetting || null);
}
